import { useCallback } from 'react';

import type { SelectProjectsResponse } from 'hooks/api/general/useGetSelectProjects';
import useAuth from 'hooks/authentication/useAuth';
import { AwardedRoleObject, CreateEditRolesAwarded, GROUPS, TECH_PROJECT_NAME } from 'hooks/permission/constants';

import type { Permission, PermissionType, ProjectNames } from './types';
import type { RoleNames } from './types';
import { getProjectNameHeader, isPermissionTypesAllowed } from './utils';
import { useAppContext } from 'store/AppProvider';

export interface PermitProps {
    projectId?: number;
    name?: Permission;
    type: keyof PermissionType;
}

export type GroupsState = Record<
    string,
    {
        role: RoleNames;
    }
>;

export interface RolePriority {
    rolePriority?: RoleNames;
    projectPriority?: string;
}

// TODO-test: be aware during covering this file because hook useAuth uses aws-amplify
// but jest can't currently fully worked with Ecmascript modules https://jestjs.io/docs/ecmascript-modules

/**
 * These features are restricted to admins of the tech project specifically, regardless of
 * which project the user is currently scoped into elsewhere — so they bypass the regular
 * priority-role/GROUPS lookup below entirely.
 */
const TECH_ADMIN_ONLY_PERMISSIONS: Permission[] = ['addons', 'global-tests'];

const usePermission = () => {
    const { appState } = useAppContext();
    const { getPermission } = useAuth();
    const groups = appState.generalData.permissionGroups;
    // name of available projects
    const availableProjects = appState.generalData.permissionAvailableProjects;
    const availableRoles = appState.generalData.permissionAvailableRoles;
    // all projects with short data
    const { projectsList } = appState.generalData;
    const priorityGroup = appState.generalData.permissionRolePriority;

    const getEditProjectList = useCallback(
        (currentPage?: Permission) => {
            const awardedRoles = currentPage ? AwardedRoleObject[currentPage] : CreateEditRolesAwarded;
            const result: SelectProjectsResponse[] = [];

            Object.entries(groups).forEach((item) => {
                if (awardedRoles?.includes(item[1].role)) {
                    const project = projectsList.find((projectInfo) => projectInfo.system_name === (item[0] as unknown as ProjectNames));

                    if (project) {
                        result.push(project);
                    }
                }
            });

            return result;
        },
        [groups, projectsList]
    );

    const getRoleList = async () => {
        const awsPermissionArray: string[] = await getPermission();
        let listOfRole: RoleNames[] = [];

        const projectWithRole = awsPermissionArray.filter((element) => element.includes(':'));

        projectWithRole.forEach((group) => {
            const groupSplit = group.split(':');

            const roleName = groupSplit[1];

            if (roleName) listOfRole = [...new Set([...listOfRole, roleName as RoleNames])];
        });

        return listOfRole;
    };

    const getIsPermit = ({ projectId, type, name }: PermitProps) => {
        if (name && TECH_ADMIN_ONLY_PERMISSIONS.includes(name)) {
            return TECH_PROJECT_NAME in groups && groups[TECH_PROJECT_NAME].role === 'admin';
        }

        const projectName = getProjectNameHeader(projectId).project_name;

        if (projectName) {
            if (!isAvailableProject(projectName as ProjectNames)) return false;
            const projectRole = groups[projectName].role;
            const permissionList = GROUPS[projectRole];

            return isPermissionTypesAllowed(permissionList, name, type);
        } else {
            const permissionList = priorityGroup.rolePriority && GROUPS[priorityGroup.rolePriority];

            return isPermissionTypesAllowed(permissionList, name, type);
        }
    };

    const isAvailableProject = (project: ProjectNames) => {
        return availableProjects.includes(project);
    };

    return {
        groups,
        projects: availableProjects,
        availableRoles,
        currentPageAvailableProjects: getEditProjectList,
        getRoleList,
        getIsPermit,
        projectPriority: priorityGroup.projectPriority,
        isAvailableProject,
    };
};

export default usePermission;
